class MusicPlayer {
  private songs: string[] = [
    '/assets/music/song1.mp3',
    '/assets/music/song2.mp3',
    '/assets/music/song3.mp3'
  ];
  private currentSongIndex: number = 0;
  private audioElement: HTMLAudioElement | null = null;
  private volume: number = 0.5; // Default volume 50%
  private isPlaying: boolean = false;
  private waitingForInteraction: boolean = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializeAudio();
      this.setupInteractionHandler();
    }
  }

  private initializeAudio() {
    this.audioElement = new Audio();
    this.audioElement.volume = this.volume;
    this.audioElement.preload = 'auto';

    // Handle song ended event to play next song
    this.audioElement.addEventListener('ended', () => {
      this.playNextSong();
    });

    // Handle loading errors
    this.audioElement.addEventListener('error', (e) => {
      console.error('Error loading audio:', e);
      this.playNextSong(); // Skip to next song if current one fails
    });
  }

  private setupInteractionHandler() {
    const startAudio = () => {
      if (this.waitingForInteraction && this.audioElement) {
        this.waitingForInteraction = false;
        this.loadAndPlayCurrentSong().catch(error => {
          if (error.name === 'NotAllowedError') {
            console.log('Waiting for user interaction to start music...');
            this.waitingForInteraction = true;
          }
        });
        
        // Remove listeners after successful start
        ['click', 'touchstart', 'keydown'].forEach(event => {
          document.removeEventListener(event, startAudio);
        });
      }
    };

    ['click', 'touchstart', 'keydown'].forEach(event => {
      document.addEventListener(event, startAudio);
    });
  }

  public play() {
    if (!this.isPlaying && this.audioElement) {
      this.isPlaying = true;
      
      // Try to play, if it fails due to no interaction, wait for it
      this.loadAndPlayCurrentSong().catch(error => {
        if (error.name === 'NotAllowedError') {
          console.log('Waiting for user interaction to start music...');
          this.waitingForInteraction = true;
        }
      });
    }
  }

  public pause() {
    if (this.audioElement && this.isPlaying) {
      this.audioElement.pause();
      this.isPlaying = false;
      this.waitingForInteraction = false;
    }
  }

  public stop() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.isPlaying = false;
      this.waitingForInteraction = false;
    }
  }

  public setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
    if (this.audioElement) {
      this.audioElement.volume = this.volume;
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  private async loadAndPlayCurrentSong() {
    if (!this.audioElement || !this.isPlaying) return;

    // Set the current song
    this.audioElement.src = this.songs[this.currentSongIndex];
    return this.audioElement.play();
  }

  private playNextSong() {
    if (!this.isPlaying) return;
    this.currentSongIndex = (this.currentSongIndex + 1) % this.songs.length;
    this.loadAndPlayCurrentSong().catch(error => {
      if (error.name === 'NotAllowedError') {
        this.waitingForInteraction = true;
      }
    });
  }

  public getCurrentSongName(): string {
    const currentSong = this.songs[this.currentSongIndex];
    return currentSong.split('/').pop()?.replace('.mp3', '') || '';
  }
}

// Create a singleton instance
export const musicPlayer = new MusicPlayer(); 